"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, Activity, Binary, FileCheck2, SlidersHorizontal, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const patScopeItems = ["Benchtop proof-of-concept testing with client-supplied formulations", "Optical probe selection & interface assessment (NIR and Raman)", "Signal-to-noise benchmarking and limit of detection (LOD) verification", "Summary report outlining inline implementation feasibility and ROI"];
const chemometricsScopeItems = ["Spectral pre-processing routines (SNV, MSC, Savitzky-Golay derivatives)", "Quantitative PLS calibration and qualitative PCA classification modeling", "Method validation aligned with ICH Q2(R2) and ASTM guidelines", "Model lifecycle support, routine recalibration, and cross-instrument transfer"];
export default function ChemometricServices() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="patandchemometricservices" data-nav="light" className="bg-muted py-16 md:py-24 text-foreground transition-colors">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Analytical Services
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            PAT Feasibility & Chemometric Modeling
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            De-risk your process analytical technology deployment with rigorous laboratory validation, 
            hardware suitability testing, and compliant chemometric calibration models.
          </p>
        </div>

        {/* 6/6 Split Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Card 1: PAT Feasibility Studies */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={cardVariants} className="h-full flex flex-col">
            <Card className="h-full border border-border shadow-xs flex flex-col justify-between hover:border-primary/40 transition-colors">
              <div>
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit">
                      <Activity className="size-6" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Phase 1 Evaluation
                    </span>
                  </div>
                  <div>
                    <CardTitle className="font-default text-2xl font-bold text-card-foreground">
                      PAT Feasibility Studies
                    </CardTitle>
                    <CardDescription className="text-muted-foreground mt-2 text-sm leading-relaxed">
                      Confirm optical compatibility, sensor positioning, and analytical viability on real 
                      matrices before capital investment or plant-scale trials.
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-card-foreground/80 flex items-center gap-1.5">
                    <FileCheck2 className="size-4 text-primary" />
                    <span>Included Scope of Work</span>
                  </div>
                  <ul className="space-y-3">
                    {patScopeItems.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="leading-snug">{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>

              <CardFooter className="pt-6 border-t border-border mt-6">
                <Button asChild className="w-full sm:w-auto" size="lg">
                  <Link to="/FeasibilityStudiesPage" className="inline-flex items-center gap-2">
                    <span>Explore Feasibility Studies</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Card 2: Chemometric Model Development */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={cardVariants} transition={{
          delay: shouldReduceMotion ? 0 : 0.15
        }} className="h-full flex flex-col">
            <Card className="h-full border border-border shadow-xs flex flex-col justify-between hover:border-primary/40 transition-colors">
              <div>
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit">
                      <Binary className="size-6" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Model Engineering
                    </span>
                  </div>
                  <div>
                    <CardTitle className="font-default text-2xl font-bold text-card-foreground">
                      Chemometric Model Development
                    </CardTitle>
                    <CardDescription className="text-muted-foreground mt-2 text-sm leading-relaxed">
                      Transform complex spectral data into robust, validated quantitative and 
                      qualitative predictors ready for release testing and real-time control.
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 pt-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-card-foreground/80 flex items-center gap-1.5">
                    <SlidersHorizontal className="size-4 text-primary" />
                    <span>Included Scope of Work</span>
                  </div>
                  <ul className="space-y-3">
                    {chemometricsScopeItems.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="leading-snug">{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>

              <CardFooter className="pt-6 border-t border-border mt-6">
                <Button asChild variant="default" className="w-full sm:w-auto" size="lg">
                  <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                    <span>Discuss Model Development</span>
                    <ChevronRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}